import React, { memo, useMemo, useState, useCallback, useEffect } from "react";
import { UserContext } from ".";
import useCache, { CACHE } from "../../hooks/useCache";
import useData from "../../hooks/useData";
import useMessage, { MESSAGE_TYPES } from "../../hooks/useMessage";
import useRemoteBrandSaver from "../../hooks/useRemoteBrandSaver";
import isArray from "../../utils/isArray";
import message from "../../utils/message";

const UserManager = memo(({ children }) => {
	const { scrapedInformation: { e: email = "", first_name = "" } = {} } = useData();

	const [user, setUser] = useState({
		email,
		first_name,
	});

	const { data: favBrands } = useCache({
		key: CACHE.FAV_BRANDS,
	});

	const hasFavBrands = useMemo(
		() => isArray(favBrands) && favBrands.length > 0,
		[favBrands]
	);

	const [shouldReIdentify, setShouldReIdentify] = useState(false);
	const hasIdentity = useMemo(() => !!user?.email, [user]);
	const updateEmail = useCallback((value) => {
		setShouldReIdentify(true);
		setUser((user) => ({ ...user, email: value }));
	}, []);

	const { updateRemoteFavBrands } = useRemoteBrandSaver({
		brandId: null,
		saved: null,
		user,
	});

	const updateSessionUser = useCallback(() => {
		if (!hasFavBrands) return;
		updateRemoteFavBrands();
	}, [updateRemoteFavBrands, hasFavBrands]);

	useEffect(() => {
		if (!shouldReIdentify) return;
		message(MESSAGE_TYPES.UPDATE_REMOTE_USER, { email: user.email });
		updateSessionUser();
		setShouldReIdentify(false);
	}, [user.email, shouldReIdentify, updateSessionUser]);

	const handleUserUpdate = useCallback((payload) => {
		if (!payload.user?.discoUuid) return;
		const {
			user: { discoUuid },
		} = payload;
		setUser((user) => ({ ...user, discoUuid }));
	}, []);
	useMessage(MESSAGE_TYPES.UPDATE_GLOBAL_STATE, handleUserUpdate);

	useEffect(() => {
		message(MESSAGE_TYPES.REQUEST_GLOBAL_STATE);
	}, []);

	return (
		<UserContext.Provider
			value={{
				user,
				setUser,
				hasIdentity,
				updateEmail,
			}}
		>
			{children}
		</UserContext.Provider>
	);
});

export default UserManager;
